import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type DistributionModalTableItemFragment,
  TimeTrackingItemOrderByColumnEnum,
} from "@src/__generated__/graphql";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  formatDate,
  LoadingOverlay,
} from "@src/components/ui-kit";
import {
  DataTable,
  TruncatedTextWithTooltip,
} from "@src/components/ui-kit/DataTable";
import { TextEditor } from "@src/components/ui-kit/TextEditor/TextEditor";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { BulkActions } from "@src/components/widgets/bulk-actions";
import { PeriodPicker } from "@src/components/widgets/PeriodPicker";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { currency } from "@src/utils/formatters/currency";
import { secondsToTime } from "@src/utils/time";
import { observer } from "mobx-react-lite";
import type { DistributionsModalStore } from "./store";

type Props = {
  readonly store: DistributionsModalStore;
};

const getColumns = (): BodyColumnDef<
  DistributionModalTableItemFragment,
  TimeTrackingItemOrderByColumnEnum
>[] => [
  {
    id: "date",
    meta: { label: t`Date` },
    header: () => t`Date`,
    orderBy: TimeTrackingItemOrderByColumnEnum.Date,
    cell: ({ row }) => formatDate(new Date(row.original.tracked_for_date)),
  },
  {
    id: "userName",
    meta: { label: t`User` },
    header: () => t`User`,
    orderBy: TimeTrackingItemOrderByColumnEnum.User,
    cell: ({ row }) => row.original.user_full_name,
  },
  {
    id: "budgetItemTitle",
    meta: { label: t`Budget item` },
    header: () => t`Budget item`,
    orderBy: TimeTrackingItemOrderByColumnEnum.OurWorkBudgetItem,
    cell: ({ row }) => row.original.our_work_budget_item_title ?? "-",
  },
  {
    id: "taskName",
    meta: { label: t`Task` },
    header: () => t`Task`,
    orderBy: TimeTrackingItemOrderByColumnEnum.Task,
    cell: ({ row }) => row.original.task_name ?? "-",
  },
  {
    id: "note",
    meta: { label: t`Note` },
    header: () => t`Note`,
    orderBy: TimeTrackingItemOrderByColumnEnum.Note,
    cell: ({ row }) => {
      const note = row.original.note ?? "-";
      const initialValue = note.replaceAll("</p><p>", " ");

      return (
        <TruncatedTextWithTooltip
          text={
            <TextEditor
              attachments={[]}
              isEditable={false}
              initialValue={initialValue}
              onChange={() => {}}
              sx={{
                p: {
                  whiteSpace: "nowrap",
                },
              }}
            />
          }
          tooltip={
            <TextEditor
              attachments={[]}
              isEditable={false}
              initialValue={note}
              onChange={() => {}}
            />
          }
        />
      );
    },
  },
  {
    id: "trackedTime",
    meta: { label: t`Tracked`, align: "right" },
    header: () => t`Tracked`,
    orderBy: TimeTrackingItemOrderByColumnEnum.TrackedHours,
    cell: ({ row }) => secondsToTime(row.original.tracked_time),
  },
  {
    id: "hourlyRate",
    meta: { label: t`Hourly Rate`, align: "right" },
    header: () => t`Hourly Rate`,
    cell: ({ row }) =>
      typeof row.original.hourly_rate === "number"
        ? currency.formatByWorkspace(row.original.hourly_rate)
        : "-",
  },
  {
    id: "total",
    meta: { label: t`Total`, align: "right" },
    header: () => t`Total`,
    cell: ({ row }) =>
      typeof row.original.amount === "number"
        ? currency.formatByWorkspace(row.original.amount)
        : "-",
  },
  {
    id: "billable",
    meta: { label: t`Billable` },
    header: () => t`Billable`,
    cell: ({ row }) => (row.original.billable ? t`Yes` : t`No`),
  },
  {
    id: ACTIONS_COLUMN_ID,
    meta: { label: t`Actions` },
  },
];

export const DistributionsModal = observer(function DistributionsModal({
  store,
}: Props) {
  return (
    <Modal
      isOpen={store.disclosureState.isOpen}
      onClose={store.disclosureState.onClose}
      size="6xl"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack justify="space-between" pr="8">
            <Text>{store.modalTitle}</Text>
            <HStack spacing="2">
              <Button onClick={store.disclosureState.onClose} variant="ghost">
                <Trans>Cancel</Trans>
              </Button>
              {store.allowSelect && (
                <Button onClick={store.handleConfirm}>
                  <Trans>Confirm</Trans>
                </Button>
              )}
            </HStack>
          </HStack>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <FiltersPanelRoot filters={store.where}>
            <HStack align="center" mb="4" spacing="2">
              <PeriodPicker
                value={store.dateRange}
                onChange={store.setDateRange}
              />

              <FiltersPanelFilters
                menuPlacement="bottom-start"
                innerMenuPlacement="right-start"
                menuListZIndex={31}
              />
            </HStack>

            <DataTable>
              <LoadingOverlay
                isLoading={store.isLoading}
                position="absolute"
                size="md"
              >
                <DataTable.Body
                  tableKey="distributions-modal"
                  data={store.tableData}
                  columns={getColumns()}
                  enableRowSelection={store.allowSelect}
                  orderedBy={store.orderBy.clauses[0]}
                  onOrderByChange={store.handleOrderChange}
                  selected={store.bulkActionsStore.selectedIdsArray}
                  onSelect={store.setSelectedTimeTrackingItemIds}
                  disableBulkActions
                />
              </LoadingOverlay>

              <Pagination store={store.pagination} />

              <BulkActions
                store={store.bulkActionsStore}
                infoBlock={
                  <HStack>
                    <Text fontWeight="medium">
                      <Trans>Total:</Trans>
                    </Text>
                    <Text fontWeight="bold">
                      {currency.formatByWorkspace(store.selectedTotalAmount)}
                    </Text>
                  </HStack>
                }
              />
            </DataTable>
          </FiltersPanelRoot>
        </ModalBody>

        <ModalFooter>
          <Button onClick={store.disclosureState.onClose} variant="ghost">
            <Trans>Cancel</Trans>
          </Button>
          {store.allowSelect && (
            <Button ml="2" onClick={store.handleConfirm}>
              <Trans>Confirm</Trans>
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
